// Avatar primitive — initials on a deterministic per-identity color, exactly
// the design's avatar language (self is always purple). Memoized: it renders
// inside video tiles (§9.3.4 — primitive props only).

import { memo, useCallback, useMemo, useState } from 'react';

const PALETTE = [
  'var(--bz-avatar-1)',
  'var(--bz-avatar-2)',
  'var(--bz-avatar-3)',
  'var(--bz-avatar-4)',
  'var(--bz-avatar-5)',
  'var(--bz-avatar-6)',
  'var(--bz-avatar-7)',
];

/** Stable color for an identity: djb2 over the identity string. */
export function avatarColor(identity: string, isSelf: boolean): string {
  if (isSelf) return 'var(--bz-avatar-self)';
  let h = 5381;
  for (let i = 0; i < identity.length; i++) h = ((h << 5) + h + identity.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length]!;
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]![0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]![0] ?? '') : (parts[0]![1] ?? '');
  return (first + last).toUpperCase();
}

interface Props {
  name: string;
  identity: string;
  isSelf?: boolean;
  /** Pixel size (design uses 30–110 depending on context). */
  size?: number;
  /** Profile photo URL; falls back to initials when absent or it fails to load. */
  photoUrl?: string | null;
}

export const Avatar = memo(function Avatar({
  name,
  identity,
  isSelf = false,
  size = 32,
  photoUrl,
}: Props) {
  const [broken, setBroken] = useState(false);
  const onError = useCallback(() => setBroken(true), []);
  const style = useMemo(
    () => ({
      width: size,
      height: size,
      fontSize: Math.round(size * 0.37),
      background: avatarColor(identity, isSelf),
    }),
    [size, identity, isSelf],
  );
  if (photoUrl && !broken) {
    return (
      <img
        className="bz-avatar bz-avatar-photo"
        src={photoUrl}
        alt=""
        aria-hidden="true"
        width={size}
        height={size}
        style={style}
        onError={onError}
      />
    );
  }
  return (
    <span className="bz-avatar" style={style} aria-hidden="true">
      {initialsOf(name)}
    </span>
  );
});
